import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../Header';
import { Footer } from '../Footer';
import styles from './SolutionPages.module.css';
import { Video, Phone, Users, CheckCircle2, ArrowRight } from 'lucide-react';
import solution41 from './solution images/solution 4.1.png';
import solution42 from './solution images/solution 4.2.png';
import solution43 from './solution images/solution 4.3.png';
import solution44 from './solution images/solution 4.4.png';
import solution51 from './solution images/solution 5.1.png';
import solution52 from './solution images/solution 5.2.png';
import solution53 from './solution images/solution 5.3.png';
import solution54 from './solution images/solution 5.4.png';
import solution61 from './solution images/solution 6.1.png';
import solution62 from './solution images/solution 6.2.png';
import solution63 from './solution images/solution 6.3.png';
import solution64 from './solution images/solution 6.4.png';

const useCaseData = {
    'online-meetings': {
        title: 'Online Meeting Intelligence',
        icon: <Video size={ 48 } />,
        color: '#8b5cf6',
        headline: 'AI Conversation Intelligence For Zoom And Google Meet Sales Conversations',
        subheadline: 'Automatically record, summarize, and analyze every online sales meeting — so insights, follow-ups, and deal signals never get lost.',
        challenge: {
            title: 'The Problem with Online Sales Meetings',
            description: 'Online meetings are now central to modern sales:',
            points: [
                'Discovery calls',
                'Product demos',
                'Pricing discussions',
                'Stakeholder reviews',
                'Renewal conversations'
            ],
            additionalText: 'But once the meeting ends:',
            additionalPoints: [
                'Notes are incomplete or missing',
                'Key objections are forgotten',
                'Follow-ups are delayed',
                'CRM updates are inconsistent',
                'Managers have zero visibility into real conversations'
            ],
            result: 'As a result, critical deal intelligence disappears the moment the call ends.'
        },
        howItHelps: {
            title: 'How Galile Turns Online Meetings into Revenue Intelligence',
            sections: [
                {
                    title: 'Automatically record and transcribe every meeting',
                    description: 'Galile joins and captures conversations across:',
                    points: ['Zoom', 'Google Meet', 'Microsoft Teams', 'Other virtual meeting platforms'],
                    footer: 'Then generates accurate transcripts and structured summaries instantly — without manual effort from reps.'
                },
                {
                    title: 'Extract deal-critical insights using AI',
                    description: 'Galile identifies:',
                    points: [
                        'Customer pain points',
                        'Budget and authority signals',
                        'Objections and competitor mentions',
                        'Buying intent and urgency',
                        'Agreed next steps'
                    ],
                    footer: 'So each meeting becomes clear, searchable deal intelligence — not just a recording.'
                },
                {
                    title: 'Generate instant summaries and follow-ups',
                    description: 'After every meeting, Galile:',
                    points: [
                        'Creates concise meeting summaries',
                        'Lists action items and responsibilities',
                        'Suggests follow-up communication',
                        'Syncs notes directly to the CRM'
                    ],
                    footer: 'Reps spend less time documenting and more time progressing deals.'
                },
                {
                    title: 'Give managers real visibility into pipeline conversations',
                    description: 'Sales leaders can:',
                    points: [
                        'Review key meeting moments quickly',
                        'Understand why deals are progressing or stuck',
                        'Coach reps using real customer interactions',
                        'Improve messaging across the team'
                    ],
                    footer: 'This transforms online meetings from black boxes into measurable pipeline signals.'
                }
            ]
        },
        outcomes: {
            title: 'Outcomes Sales Teams Experience',
            points: [
                'Faster post-meeting follow-ups',
                'Higher demo-to-close conversion rates',
                'Cleaner, automatically updated CRM data',
                'Better coaching for new and existing reps',
                'More predictable pipeline forecasting'
            ]
        },
        whoFor: {
            title: 'Who This Is For',
            roles: [
                'SaaS and tech sales teams',
                'Inside sales and remote selling teams',
                'SDR and AE organizations',
                'Customer success and renewal teams',
                'Founder-led online sales motions'
            ]
        },
        ctaText: 'Make every online meeting count toward revenue.'
    },
    'phone-intelligence': {
        title: 'Phone Conversation Intelligence',
        icon: <Phone size={ 48 } />,
        color: '#ef4444',
        headline: 'AI Conversation Intelligence For Phone-First Sales Teams',
        subheadline: 'Automatically record, transcribe, analyze, and summarize every sales call — so follow-ups are never missed and managers always know what\'s happening.',
        challenge: {
            title: 'The Reality of Phone-First Sales',
            description: 'For many Indian SMBs, sales happens primarily on phone calls — not inside the CRM.',
            additionalText: 'But phone conversations are:',
            additionalPoints: [
                'Rarely recorded',
                'Poorly documented',
                'Impossible to review at scale',
                'Lost once the call ends'
            ],
            result: 'This creates: Missed follow-ups, Inconsistent customer communication, No coaching visibility, Unreliable pipeline forecasting. Revenue decisions happen on calls — but insights never reach the business.'
        },
        howItHelps: {
            title: 'How Galile Brings Intelligence to Every Sales Call',
            sections: [
                {
                    title: 'Automatically capture and transcribe calls',
                    description: 'Galile records and transcribes:',
                    points: [
                        'Outbound prospecting calls',
                        'Inbound lead conversations',
                        'Follow-up and negotiation calls',
                        'Renewal and upsell discussions'
                    ]
                },
                {
                    title: 'Turn conversations into structured insights',
                    description: 'Galile\'s AI identifies:',
                    points: [
                        'Customer intent and urgency',
                        'Pricing objections',
                        'Competitor mentions',
                        'Decision timelines',
                        'Next steps and commitments'
                    ],
                    footer: 'So every call becomes actionable pipeline intelligence, not just audio.'
                },
                {
                    title: 'Never miss a follow-up again',
                    description: 'After each call, Galile:',
                    points: [
                        'Generates clear summaries',
                        'Extracts action items',
                        'Creates follow-up reminders',
                        'Syncs notes to the CRM'
                    ],
                    footer: 'Reps spend less time writing notes and more time closing deals.'
                },
                {
                    title: 'Coach teams using real conversations',
                    description: 'Managers can:',
                    points: [
                        'Review key call moments instantly',
                        'Compare talk ratios across reps',
                        'Identify winning scripts',
                        'Train new hires faster'
                    ],
                    footer: 'All using actual customer conversations, not assumptions.'
                }
            ]
        },
        outcomes: {
            title: 'Outcomes Phone-First Teams See Quickly',
            points: [
                'Higher lead-to-conversion rates',
                'Faster follow-ups',
                'Better call quality across reps',
                'Cleaner CRM records',
                'Improved forecast visibility'
            ]
        },
        whoFor: {
            title: 'Who This Is For',
            roles: [
                'Inside sales teams',
                'Tele-calling and telesales operations',
                'SMB founders running phone-based sales',
                'Lead qualification teams',
                'Renewal and upsell teams'
            ]
        },
        ctaText: 'Make every sales call measurable, searchable, and actionable.'
    },
    'field-sales': {
        title: 'In-Person Meeting Intelligence',
        icon: <Users size={ 48 } />,
        color: '#0ea5e9',
        headline: 'Finally Capture What Happens In Field Sales Meetings.',
        subheadline: 'Record in-person discussions, generate instant summaries, and sync everything to your CRM — without extra work for reps.',
        challenge: {
            title: 'The Hidden Blind Spot in Field Sales',
            description: 'Field sales drives major revenue in India — but visibility is almost zero.',
            points: [
                'Meetings happen in person',
                'Notes are written later (or never)',
                'Managers rely on verbal updates',
                'CRM entries are incomplete',
                'Validation and auditing is difficult'
            ],
            result: 'This creates pipeline blind spots and lost revenue opportunities.'
        },
        howItHelps: {
            title: 'How Galile Brings Intelligence to Field Sales',
            sections: [
                {
                    title: 'Capture in-person conversations effortlessly',
                    description: 'Reps can:',
                    points: [
                        'Record live meeting discussions',
                        'Upload voice notes',
                        'Generate instant AI summaries',
                        'Share structured updates with managers'
                    ],
                    footer: 'No manual typing required.'
                },
                {
                    title: 'Turn meetings into actionable next steps',
                    description: 'Galile extracts:',
                    points: [
                        'Customer requirements',
                        'Pricing discussions',
                        'Decision timelines',
                        'Risks and objections',
                        'Follow-up commitments'
                    ],
                    footer: 'Every visit becomes structured pipeline data.'
                },
                {
                    title: 'Give managers real visibility into the field',
                    description: 'Leaders can:',
                    points: [
                        'See what actually happened in meetings',
                        'Track visit outcomes across territories',
                        'Identify stalled deals early',
                        'Coach reps using real conversations'
                    ]
                },
                {
                    title: 'Improve accountability without micromanaging',
                    points: [
                        'Automatic visit logs',
                        'Clear follow-up tasks',
                        'Consistent CRM updates',
                        'Transparent deal progress'
                    ]
                }
            ]
        },
        outcomes: {
            title: 'Business Impact',
            points: [
                'Higher conversion from field visits',
                'Fewer missed follow-ups',
                'Faster deal progression',
                'Better territory visibility',
                'Stronger forecasting confidence'
            ]
        },
        whoFor: {
            title: 'Best Fit For',
            roles: [
                'Distribution sales teams',
                'Channel & partner sales',
                'Manufacturing & industrial sales',
                'Real estate & on-site selling',
                'Enterprise field reps'
            ]
        },
        ctaText: 'Turn every field meeting into measurable pipeline progress.'
    }
};

const SolutionUseCases = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const data = useCaseData[slug];

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [slug]);

    if (!data) {
        return (
            <div className={ styles.container }>
                <Header />
                <div className={ styles.notFound }>
                    <h1>Use Case Not Found</h1>
                    <button onClick={ () => navigate('/') }>Return Home</button>
                </div>
                <Footer />
            </div>
        );
    }

    return (
        <div className={ styles.container }>
            <Header />

            <main className={ styles.main }>
                {/* Hero Section */ }
                <section className={ styles.hero } style={ { '--accent-color': data.color } }>
                    <div className={ styles.heroContent }>
                        <div className={ styles.iconBox }>{ data.icon }</div>
                        <h1 className={ styles.headline }>{ data.headline }</h1>
                        <p className={ styles.subheadline }>{ data.subheadline }</p>
                        <a href="https://calendly.com/viraj-galile/30min" target="_blank" rel="noopener noreferrer" style={ { textDecoration: 'none' } }>
                            <button className={ styles.ctaButton }>
                                { slug === 'field-sales' ? 'See it in action' : 'Book a Demo' }
                            </button>
                        </a>
                    </div>
                </section>

                {/* Challenge Section */ }
                <section className={ styles.section }>
                    <div className={ styles.contentWrapper }>
                        <h2 className={ styles.sectionTitle }>{ data.challenge.title }</h2>
                        <p className={ styles.description }>{ data.challenge.description }</p>
                        
                        <div className={ styles.problemContainer }>
                            <div className={ styles.problemGrid }>
                                { data.challenge.points && (
                                    <div className={ styles.problemCard }>
                                        <div className={ styles.problemCardHeader }>
                                            <div className={ styles.problemCardIcon }>📋</div>
                                            <h3 className={ styles.problemCardTitle }>Key Challenges</h3>
                                        </div>
                                        <ul className={ styles.bulletList }>
                                            { data.challenge.points.map((point, i) => (
                                                <li key={ i }>{ point }</li>
                                            )) }
                                        </ul>
                                    </div>
                                ) }
                                
                                { data.challenge.additionalPoints && (
                                    <div className={ styles.problemCard }>
                                        <div className={ styles.problemCardHeader }>
                                            <div className={ styles.problemCardIcon }>⚠️</div>
                                            <h3 className={ styles.problemCardTitle }>{ data.challenge.additionalText || 'Impact' }</h3>
                                        </div>
                                        <ul className={ styles.bulletList }>
                                            { data.challenge.additionalPoints.map((point, i) => (
                                                <li key={ i }>{ point }</li>
                                            )) }
                                        </ul>
                                    </div>
                                ) }
                            </div>
                            
                            <div className={ styles.resultBox }>
                                <div className={ styles.resultIcon }>💡</div>
                                <p className={ styles.resultText }>{ data.challenge.result }</p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* How It Helps Section - Zigzag Layout */ }
                <section className={ styles.sectionAlt }>
                    <div className={ styles.contentWrapper }>
                        <h2 className={ styles.sectionTitle }>{ data.howItHelps.title }</h2>
                        <div className={ styles.zigzagContainer }>
                            { data.howItHelps.sections.map((section, i) => (
                                <div key={ i } className={ `${styles.zigzagRow} ${i % 2 === 1 ? styles.zigzagRowReverse : ''}` }>
                                    <div className={ styles.zigzagContent }>
                                        <h3 className={ styles.zigzagTitle }>{ section.title }</h3>
                                        { section.description && <p className={ styles.zigzagDesc }>{ section.description }</p> }
                                        <ul className={ styles.zigzagList }>
                                            { section.points.map((point, j) => (
                                                <li key={ j }>
                                                    <CheckCircle2 size={ 18 } className={ styles.checkIcon } />
                                                    <span>{ point }</span>
                                                </li>
                                            )) }
                                        </ul>
                                        { section.footer && <p className={ styles.zigzagFooter }>{ section.footer }</p> }
                                    </div>
                                    <div className={ styles.zigzagImage }>
                                        <div className={ styles.imagePlaceholder }>
                                            { slug === 'online-meetings' && i === 0 ? (
                                                <img src={ solution41 } alt="Video Meeting Auto-Recording" style={ { width: '100%', height: '100%', objectFit: 'cover', borderRadius: '12px', border: '1px solid #e5e7eb' } } />
                                            ) : slug === 'online-meetings' && i === 1 ? (
                                                <img src={ solution42 } alt="AI Deal Intelligence Extraction" style={ { width: '100%', height: '100%', objectFit: 'contain', borderRadius: '12px', border: '1px solid #e5e7eb' } } />
                                            ) : slug === 'online-meetings' && i === 2 ? (
                                                <img src={ solution43 } alt="Instant Summaries & Follow-ups" style={ { width: '100%', height: '100%', objectFit: 'cover', borderRadius: '12px', border: '1px solid #e5e7eb' } } />
                                            ) : slug === 'online-meetings' && i === 3 ? (
                                                <img src={ solution44 } alt="Pipeline Conversation Visibility" style={ { width: '100%', height: '100%', objectFit: 'contain', borderRadius: '12px', border: '1px solid #e5e7eb' } } />
                                            ) : slug === 'phone-intelligence' && i === 0 ? (
                                                <img src={ solution51 } alt="Call Recording & Transcription" style={ { width: '100%', height: '100%', objectFit: 'cover', borderRadius: '12px', border: '1px solid #e5e7eb' } } />
                                            ) : slug === 'phone-intelligence' && i === 1 ? (
                                                <img src={ solution52 } alt="Structured Insights Dashboard" style={ { width: '100%', height: '100%', objectFit: 'contain', borderRadius: '12px', border: '1px solid #e5e7eb' } } />
                                            ) : slug === 'phone-intelligence' && i === 2 ? (
                                                <img src={ solution53 } alt="Automated Follow-ups & CRM Sync" style={ { width: '100%', height: '100%', objectFit: 'cover', borderRadius: '12px', border: '1px solid #e5e7eb' } } />
                                            ) : slug === 'phone-intelligence' && i === 3 ? (
                                                <img src={ solution54 } alt="Team Coaching & Talk-Ratios" style={ { width: '100%', height: '100%', objectFit: 'cover', borderRadius: '12px', border: '1px solid #e5e7eb' } } />
                                            ) : slug === 'field-sales' && i === 0 ? (
                                                <img src={ solution61 } alt="In-Person Meeting Capture" style={ { width: '100%', height: '100%', objectFit: 'contain', borderRadius: '12px', border: '1px solid #e5e7eb' } } />
                                            ) : slug === 'field-sales' && i === 1 ? (
                                                <img src={ solution62 } alt="Actionable Next Steps Extraction" style={ { width: '100%', height: '100%', objectFit: 'contain', borderRadius: '12px', border: '1px solid #e5e7eb' } } />
                                            ) : slug === 'field-sales' && i === 2 ? (
                                                <img src={ solution63 } alt="Field Visibility Dashboard" style={ { width: '100%', height: '100%', objectFit: 'cover', borderRadius: '12px', border: '1px solid #e5e7eb' } } />
                                            ) : slug === 'field-sales' && i === 3 ? (
                                                <img src={ solution64 } alt="Accountability & CRM Updates" style={ { width: '100%', height: '100%', objectFit: 'cover', borderRadius: '12px', border: '1px solid #e5e7eb' } } />
                                            ) : (
                                                <div className={ styles.placeholderIcon } style={ { background: '#f3f4f6', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '12px', border: '1px solid #e5e7eb' } }>
                                                    { i === 0 && <Video size={ 48 } /> }
                                                    { i === 1 && <CheckCircle2 size={ 48 } /> }
                                                    { i === 2 && <Phone size={ 48 } /> }
                                                    { i === 3 && <Users size={ 48 } /> }
                                                </div>
                                            ) }
                                        </div>
                                    </div>
                                </div>
                            )) }
                        </div>
                    </div>
                </section>

                {/* Outcomes Section */ }
                <section className={ styles.section }>
                    <div className={ styles.contentWrapper }>
                        <h2 className={ styles.sectionTitle }>{ data.outcomes.title }</h2>
                        <div className={ styles.outcomesGrid }>
                            { data.outcomes.points.map((point, i) => (
                                <div key={ i } className={ styles.outcomeCard }>
                                    <CheckCircle2 size={ 24 } className={ styles.outcomeIcon } />
                                    <span>{ point }</span>
                                </div>
                            )) }
                        </div>
                    </div>
                </section>

                {/* Who For Section */ }
                <section className={ styles.sectionAlt }>
                    <div className={ styles.contentWrapper }>
                        <h2 className={ styles.sectionTitle }>{ data.whoFor.title }</h2>
                        <div className={ styles.rolesList }>
                            { data.whoFor.roles.map((role, i) => (
                                <div key={ i } className={ styles.roleItem }>
                                    <span className={ styles.roleBullet }>•</span>
                                    <span>{ role }</span>
                                </div>
                            )) }
                        </div>
                    </div>
                </section>

                {/* CTA Section */ }
                <section className={ styles.ctaSection }>
                    <div className={ styles.ctaCard }>
                        <h2>{ data.ctaText }</h2>
                        <a href="https://calendly.com/viraj-galile/30min" target="_blank" rel="noopener noreferrer" style={ { textDecoration: 'none' } }>
                            <button className={ styles.ctaButtonLarge }>
                                Book a Demo <ArrowRight size={ 20 } />
                            </button>
                        </a>
                    </div>
                </section>
            </main>

            <Footer />
        </div>
    );
};

export default SolutionUseCases;
